import { Icons } from "assets";
import classNames from "classnames";
import moment from "moment";
import { useEffect, useState } from "react";
import CustomButton from "shared/components/common/customButton";
import CustomPagination from "shared/components/common/customPagination";
import CustomTable from "shared/components/common/customTable";
import useDebounce from "shared/hooks/useDebounce";
import SekeletonLoader from "shared/loaders/skeleton";
import styles from "./styles.module.scss";
import {
  GetArchiveCountService,
  GetDraftEvaluationsService,
} from "shared/services/evaluations";
import { JobRole } from "shared/utils/enum";
import { useNavigate } from "react-router-dom";
import { routeConstant } from "shared/routes/routeConstant";
import { useDispatch } from "react-redux";
import { setArchiveCountReducer } from "shared/redux/reducers/archiveCount";

const Heads: CustomTableHeadType[] = [
  {
    title: "Job Title",
    key: "jobTitle",
  },
  {
    title: "Department",
    key: "department",
  },
  {
    title: "Job Code",
    key: "jobCode",
  },
  {
    title: "Role Type",
    key: "roleType",
  },
  {
    title: "Evaluated By",
    key: "evaluatedBy",
  },
  {
    title: "Evaluated Date",
    key: "evaluationDate",
  },
  {
    title: "Action",
    key: "",
  },
];

const Drafts = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(true);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [search, setSearch] = useState<string>("");
  const [searchVal, setSearchVal] = useState<string>("");
  const [page, setPage] = useState<number>(1);
  const [total, setTotal] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>("");
  const [order, setOrder] = useState<string>("");

  const handleGetCount = () => {
    GetArchiveCountService()
      .then(({ data }) => {
        dispatch(setArchiveCountReducer({ count: data }));
      })
      .catch((err) => {});
  };

  const handleGetEvaluations = (page: number) => {
    setLoading(true);
    GetDraftEvaluationsService({
      search: searchVal,
      pageNo: page,
      sortBy,
      order,
    })
      .then(({ data: { data, total } }) => {
        setEvaluations(data);
        setTotal(total);
      })
      .catch((err) => {})
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setPage(1);
    setEvaluations([]);
    setLoading(true);
    handleGetEvaluations(1);
    // eslint-disable-next-line
  }, [searchVal, sortBy, order]);

  useEffect(() => {
    handleGetCount();
    // eslint-disable-next-line
  }, []);

  useDebounce(
    () => {
      setSearchVal(search);
    },
    [search],
    800
  );

  return (
    <div className={classNames(styles.mainContainer)}>
      <div
        className={classNames(
          "flex items-start gap-3 lg:items-center justify-between flex-col lg:flex-row px-4 py-4",
          styles.tableMainHead
        )}
      >
        <div className={classNames("flex flex-col items-start")}>
          <h1 className={classNames(styles.tableMainHeading)}>
            Draft Evaluations
          </h1>
          <p className={classNames(styles.tableMainTitle)}>
            Complete your pending evaluations
          </p>
        </div>
        <div className={classNames("flex flex-wrap items-center gap-3")}>
          <div className={classNames(styles.searchContainer, "flex")}>
            <Icons.Search className={classNames(styles.searchIconStyle)} />
            <input
              className={classNames(styles.searchInputStyle, "ps-2")}
              value={search}
              placeholder="Search Job Title / Dept"
              onChange={(e) => {
                setSearch(e.target.value);
              }}
            />
          </div>
        </div>
      </div>
      <CustomTable
        title="Draft Evaluations"
        heads={Heads}
        loading={loading}
        isEmpty={evaluations.length === 0}
        stickSecondCol={false}
        setSortBy={setSortBy}
        setOrder={setOrder}
        sortBy={sortBy}
        order={order}
      >
        {loading ? (
          <>
            {Array.from(Array(5).keys()).map((_, index) => {
              return <TableRowLoader key={index} />;
            })}
          </>
        ) : (
          <>
            {evaluations.map((evaluation, index) => {
              return <TableRow item={evaluation} key={index} index={index} />;
            })}
          </>
        )}
      </CustomTable>

      <div
        className={classNames(
          "w-full px-4 py-4",
          total >= 10 && styles.border,
          styles.paginationContainer
        )}
      >
        <CustomPagination
          currentPage={page}
          totalCount={total}
          pageSize={10}
          onPageChange={(page: any) => {
            setPage(page);
            handleGetEvaluations(page);
          }}
        />
      </div>
    </div>
  );
};

const TableRow = ({ item, index }: any) => {
  const navigate = useNavigate();
  return (
    <>
      <tr>
        <td
          className={classNames(
            styles.tableContentLabel,
            "ps-4 pe-2 py-3 sticky-col first-col"
          )}
        >
          {item?.current_version?.jobTitle}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.current_version?.department}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.jobCode}
        </td>

        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.current_version?.roleType === JobRole.management
            ? "Management"
            : "Individual Contributor"}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {item?.evaluated_by?.name}
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          {moment(item?.current_version?.created_at).format("MMM DD, YYYY")}
        </td>
        <td
          className={classNames(styles.tableContentLabel, "ps-2 pe-4 py-3")}
          style={{ verticalAlign: "middle", textAlign: "center" }}
        >
          <div className={classNames("flex items-center gap-2 justify-center")}>
            <CustomButton
              title="Continue"
              Icon={Icons.Pencil}
              IconDirction="right"
              containerStyle={classNames(styles.actionButton)}
              iconStyle={classNames(styles.actionIcon)}
              onClick={() => {
                navigate(
                  routeConstant.continueDraftEvaluation.path.replace(
                    ":id",
                    item.currentVersion
                  )
                );
              }}
            />
          </div>
        </td>
      </tr>
    </>
  );
};
const TableRowLoader = () => {
  return (
    <>
      <tr>
        <td className={classNames(styles.tableContentLabel, "ps-4 pe-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td className={classNames(styles.tableContentLabel, "px-2 py-3")}>
          <div className={classNames("flex items-center justify-center")}>
            <SekeletonLoader className={classNames(styles.lineLoader)} />
          </div>
        </td>
        <td
          className={classNames(styles.tableContentLabel, "ps-2 pe-4 py-3")}
          style={{ verticalAlign: "middle" }}
        >
          <div className={classNames("flex items-center justify-center gap-2")}>
            <SekeletonLoader
              className={classNames(styles.actionContainerLoader)}
            />
          </div>
        </td>
      </tr>
    </>
  );
};

export default Drafts;
